import React from 'react';
import { Box, Container, Typography, Paper, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupsIcon from '@mui/icons-material/Groups';
import EmailIcon from '@mui/icons-material/Email';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SEOHead from '../components/SEO/SEOHead';

const HeroSection = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, #1a2e4a 0%, #2a4a6e 100%)',
    color: '#ffffff',
    padding: theme.spacing(8, 2),
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30z' fill='%23ffffff' fill-opacity='0.02'/%3E%3C/svg%3E")`,
        backgroundSize: '60px 60px',
    },
}));

const HeroBadge = styled(Box)(({ theme }) => ({
    display: 'inline-block',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    padding: theme.spacing(0.75, 2),
    borderRadius: '2rem',
    fontSize: '0.8rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(3),
    color: '#ffffff',
}));

const HeroCta = styled('a')(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: theme.palette.orange?.main || '#e67e22',
    color: '#ffffff',
    padding: theme.spacing(1.5, 3),
    borderRadius: '4px',
    textDecoration: 'none',
    fontWeight: 500,
    fontSize: '1rem',
    transition: 'all 0.2s ease',
    marginTop: theme.spacing(3),
    '&:hover': {
        backgroundColor: theme.palette.orange?.dark || '#d35400',
        transform: 'translateY(-2px)',
    },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    position: 'relative',
    paddingBottom: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '40px',
        height: '3px',
        backgroundColor: theme.palette.orange?.main || '#e67e22',
        borderRadius: '2px',
    },
}));

const StepCard = styled(Paper)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
    padding: theme.spacing(2.5),
    borderRadius: '8px',
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.2s ease',
    '&:hover': {
        borderColor: theme.palette.orange?.main || '#e67e22',
        boxShadow: '0 4px 12px rgba(26, 46, 74, 0.08)',
    },
}));

const StepNumber = styled(Box)(({ theme }) => ({
    flexShrink: 0,
    width: '36px',
    height: '36px',
    backgroundColor: '#1a2e4a',
    color: '#ffffff',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '0.9rem',
}));

const BenefitCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    borderRadius: '8px',
    border: `1px solid ${theme.palette.divider}`,
    height: '100%',
}));

const BenefitIcon = styled(Box)(({ theme }) => ({
    width: '32px',
    height: '32px',
    backgroundColor: 'rgba(230, 126, 34, 0.1)',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
    color: theme.palette.orange?.main || '#e67e22',
}));

const CtaSection = styled(Box)(({ theme }) => ({
    backgroundColor: '#1a2e4a',
    color: '#ffffff',
    padding: theme.spacing(6, 2),
    textAlign: 'center',
    marginTop: theme.spacing(6),
}));

const CtaEmail = styled('a')(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: '#ffffff',
    color: '#1a2e4a',
    padding: theme.spacing(1.5, 3),
    borderRadius: '4px',
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: '1.05rem',
    transition: 'all 0.2s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
    },
}));

const MarinaBenefitPage: React.FC = () => {
    const steps = [
        {
            title: 'Your marina receives a private discount code',
            description: 'A unique code tied to your marina—share it however you like.',
        },
        {
            title: 'Boaters use the code at pointsails.com',
            description: 'Preferred pricing is applied automatically at checkout.',
        },
        {
            title: 'Pricing is shown instantly',
            description: 'No waiting for quotes. Configure and see costs in real time.',
        },
        {
            title: 'We handle everything else',
            description: 'Design review, production, and delivery—the marina is never involved.',
        },
    ];

    const benefits = [
        {
            icon: <AttachMoneyIcon fontSize="small" />,
            title: 'Real Value',
            description: "Sails aren't cheap. Boaters notice when they save.",
        },
        {
            icon: <CheckCircleOutlineIcon fontSize="small" />,
            title: 'Zero Admin',
            description: 'No orders to manage. No questions to field.',
        },
        {
            icon: <AccessTimeIcon fontSize="small" />,
            title: 'No Expiration',
            description: 'Boaters use it when they need it. No pressure.',
        },
        {
            icon: <GroupsIcon fontSize="small" />,
            title: 'Reflects Well',
            description: 'A benefit that shows the marina cares about its boaters.',
        },
    ];

    const features = [
        'Proven sail design',
        'Premium materials',
        'Transparent pricing',
        'Online ordering',
    ];

    return (
        <>
            <SEOHead 
                page="marina-benefit"
                customMeta={{
                    title: "Marina Customer Benefit | Point Sails",
                    description: "Preferred pricing on custom cruising and racing sails for marina customers. A no-maintenance benefit your boaters will actually use.",
                    keywords: "marina discount, boater sails, marina benefit, custom sails discount"
                }}
            />
            
            {/* Hero Section */}
            <HeroSection>
                <Box sx={{ position: 'relative', maxWidth: '800px', margin: '0 auto' }}>
                    <HeroBadge>Marina Partner Program</HeroBadge>
                    <Typography 
                        variant="h2" 
                        sx={{ 
                            fontWeight: 500, 
                            mb: 2,
                            fontSize: { xs: '2rem', md: '2.75rem' },
                            color: '#ffffff',
                        }}
                    >
                        A Simple Benefit for Marina Customers
                    </Typography>
                    <Typography 
                        sx={{ 
                            fontSize: '1.15rem', 
                            fontWeight: 300, 
                            opacity: 0.9,
                            maxWidth: '600px',
                            margin: '0 auto',
                            color: '#ffffff',
                        }}
                    >
                        Preferred pricing on custom cruising and racing sails—offered as a no-maintenance benefit your boaters will actually use.
                    </Typography>
                    <HeroCta href="#get-started">
                        Get Your Marina Code
                        <ArrowForwardIcon sx={{ fontSize: 18 }} />
                    </HeroCta>
                </Box>
            </HeroSection>

            {/* Main Content */}
            <Container maxWidth="md" sx={{ py: 6 }}>
                {/* What This Is */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle variant="h4">What This Is</SectionTitle>
                    <Typography sx={{ fontSize: '1.05rem', mb: 2 }}>
                        Point Sails offers marinas a straightforward way to provide customers with preferred pricing on custom sails—without creating work for the marina.
                    </Typography>
                    <Typography sx={{ fontSize: '1.05rem' }}>
                        There are no obligations, no reporting, and no follow-up required. It's simply a benefit boaters can use when they're ready for new sails.
                    </Typography>
                </Box>

                {/* How It Works */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle variant="h4">How It Works</SectionTitle>
                    <Stack spacing={2}>
                        {steps.map((step, index) => (
                            <StepCard key={index} elevation={0}>
                                <StepNumber>{index + 1}</StepNumber>
                                <Box>
                                    <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 600, mb: 0.5 }}>
                                        {step.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {step.description}
                                    </Typography>
                                </Box>
                            </StepCard>
                        ))}
                    </Stack>
                </Box>

                {/* Why Marinas Offer This */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle variant="h4">Why Marinas Offer This</SectionTitle>
                    <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                        gap: 2,
                    }}>
                        {benefits.map((benefit, index) => (
                            <BenefitCard key={index} elevation={0}>
                                <BenefitIcon>{benefit.icon}</BenefitIcon>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {benefit.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {benefit.description}
                                </Typography>
                            </BenefitCard>
                        ))}
                    </Box>
                </Box>

                {/* About Point Sails */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle variant="h4">About Point Sails</SectionTitle>
                    <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                        <Typography sx={{ fontSize: '1.05rem', mb: 2 }}>
                            Point Sails designs and builds custom cruising and racing sails using premium Challenge sailcloth. Sail design is handled by a sailmaker with over 30 years of experience. Sails are built in the same overseas lofts trusted by some of the largest brands in the industry.
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            {features.map((feature, index) => (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <CheckIcon sx={{ fontSize: 18, color: 'orange.main' }} />
                                    <Typography variant="body2">{feature}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Paper>
                </Box>

                {/* What Boaters Can Order */}
                <Box>
                    <SectionTitle variant="h4">What Boaters Can Order</SectionTitle>
                    <Typography sx={{ fontSize: '1.05rem' }}>
                        The program covers custom cruising sails and racing sails for keelboats and trailer-sailers. Beach cat sails are not included.
                    </Typography>
                </Box>
            </Container>

            {/* CTA Section */}
            <CtaSection id="get-started">
                <Typography variant="h4" sx={{ mb: 1.5, color: '#ffffff' }}>
                    Ready to Set This Up?
                </Typography>
                <Typography sx={{ opacity: 0.9, fontSize: '1.05rem', maxWidth: '500px', margin: '0 auto', mb: 3, color: '#ffffff' }}>
                    Getting started is simple. We'll create your private marina code and provide clean materials you can share however you prefer.
                </Typography>
                <CtaEmail href="mailto:daron@pointsails.com?subject=Marina%20Partner%20Program">
                    <EmailIcon sx={{ color: 'orange.main' }} />
                    daron@pointsails.com
                </CtaEmail>
                <Typography sx={{ mt: 2, fontSize: '0.9rem', opacity: 0.7, color: '#ffffff' }}>
                    Subject: Marina Partner Program
                </Typography>
            </CtaSection>
        </>
    );
};

export default MarinaBenefitPage;
